import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchPathViewerState } from "@/api/fetchPathViewerState";
import { completePathItem } from "@/api/completePathItem";
import { completeChecklistItem } from "@/api/completeChecklistItem";
import { completeMeetingTask } from "@/api/completeMeetingTask";
import { completeDecisionTask } from "@/api/completeDecisionTask";
import { completeQuizTask } from "@/api/completeQuizTask";
import { completeProfileTask } from "@/api/completeProfileTask";
import { uploadPathItemFile } from "@/api/uploadPathItemFile";
import { updateLastAccessed } from "@/api/updateLastAccessed";
import type { UpdateLastAccessedResponse } from "@/api/updateLastAccessed";
import type { ViewerState } from "../types/viewerState";
import { diffCelebrations } from "../utils/viewerState";

export interface PathViewerSliceState {
  /** Viewer-state v2 payload, stored verbatim (server statuses are authoritative). */
  currentPath: ViewerState | null;
  loading: boolean;
  error: string | null;
  completingItemId: number | null;
  completingChecklistItemIndex: number | null;
  uploadingItemId: number | null;
  /** Milestone items revealed as newly completed by a completion refetch (FIFO queue). */
  celebrationMilestoneItemIds: number[];
  /** Set when a completion refetch flips progress.pathComplete to true. */
  pathJustCompleted: boolean;
}

const initialState: PathViewerSliceState = {
  currentPath: null,
  loading: false,
  error: null,
  completingItemId: null,
  completingChecklistItemIndex: null,
  uploadingItemId: null,
  celebrationMilestoneItemIds: [],
  pathJustCompleted: false,
};

interface ThunkStateSlice {
  pathViewer: PathViewerSliceState;
}

const extractApiError = (err: unknown, fallback: string): string => {
  const axiosError = err as { response?: { data?: { error?: string } } };
  if (axiosError?.response?.data?.error) {
    return axiosError.response.data.error;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
};

const requireLoadedEnrollment = (state: ThunkStateSlice) => {
  const currentPath = state.pathViewer.currentPath;
  if (!currentPath) {
    throw new Error("Learning path is not loaded");
  }
  return currentPath;
};

export const loadPathViewerState = createAsyncThunk(
  "pathViewer/loadPathViewerState",
  async (pathId: number, { rejectWithValue }) => {
    try {
      return await fetchPathViewerState(pathId);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to load learning path"),
      );
    }
  },
);

export const completePathItemThunk = createAsyncThunk(
  "pathViewer/completePathItem",
  async (
    {
      itemId,
      completionData,
    }: { itemId: number; completionData?: Record<string, unknown> },
    { getState, rejectWithValue },
  ) => {
    try {
      const { path } = requireLoadedEnrollment(getState() as ThunkStateSlice);
      await completePathItem(itemId, completionData);
      // Refetch so the server-evaluated statuses (incl. milestone
      // auto-complete fixpoint) replace the stale snapshot.
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(extractApiError(err, "Failed to complete item"));
    }
  },
);

export const completeChecklistItemThunk = createAsyncThunk(
  "pathViewer/completeChecklistItem",
  async (
    { pathItemId, itemIndex }: { pathItemId: number; itemIndex: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const { enrollment, path } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      // enrollment.userId comes from the server payload; the backend derives
      // the actor from the token and ignores body-supplied user ids.
      await completeChecklistItem(pathItemId, itemIndex, {
        enrollmentId: enrollment.id,
        completedByUserId: enrollment.userId,
      });
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to complete checklist item"),
      );
    }
  },
);

export const completeMeetingTaskThunk = createAsyncThunk(
  "pathViewer/completeMeetingTask",
  async (
    {
      pathItemId,
      meetingScheduledFor,
      notes,
    }: { pathItemId: number; meetingScheduledFor?: string; notes?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { enrollment, path } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      await completeMeetingTask(pathItemId, {
        enrollmentId: enrollment.id,
        completedByUserId: enrollment.userId,
        meetingScheduledFor,
        notes,
      });
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to complete meeting task"),
      );
    }
  },
);

export const completeDecisionTaskThunk = createAsyncThunk(
  "pathViewer/completeDecisionTask",
  async (
    { pathItemId, choice }: { pathItemId: number; choice: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { enrollment, path } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      await completeDecisionTask(pathItemId, {
        enrollmentId: enrollment.id,
        choice,
      });
      // Refetch: the recorded choice re-prunes branches, so the visible item
      // set (and resume pointer) changes — only the server knows the new shape.
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to record your choice"),
      );
    }
  },
);

export const completeQuizTaskThunk = createAsyncThunk(
  "pathViewer/completeQuizTask",
  async (
    { pathItemId, score }: { pathItemId: number; score: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const { enrollment, path } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      await completeQuizTask(pathItemId, {
        enrollmentId: enrollment.id,
        score,
      });
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to record your quiz result"),
      );
    }
  },
);

export const completeProfileTaskThunk = createAsyncThunk(
  "pathViewer/completeProfileTask",
  async (
    { pathItemId }: { pathItemId: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const { enrollment, path } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      await completeProfileTask(pathItemId, {
        enrollmentId: enrollment.id,
        completedByUserId: enrollment.userId,
      });
      return await fetchPathViewerState(path.id);
    } catch (err) {
      // 422 carries the missing-profile-fields contract from completeProfileTask.
      const incomplete = err as {
        status?: number;
        data?: { missingFields?: string[] };
      };
      if (incomplete?.status === 422) {
        const missing = incomplete.data?.missingFields ?? [];
        return rejectWithValue(
          missing.length > 0
            ? `Complete your profile first — missing: ${missing.join(", ")}`
            : "Complete your profile before finishing this task",
        );
      }
      return rejectWithValue(
        extractApiError(err, "Failed to complete profile task"),
      );
    }
  },
);

export const uploadPathItemFileThunk = createAsyncThunk(
  "pathViewer/uploadPathItemFile",
  async (
    { itemId, file }: { itemId: number; file: File },
    { getState, rejectWithValue },
  ) => {
    try {
      const { path } = requireLoadedEnrollment(getState() as ThunkStateSlice);
      await uploadPathItemFile(itemId, file);
      return await fetchPathViewerState(path.id);
    } catch (err) {
      return rejectWithValue(extractApiError(err, "Failed to upload file"));
    }
  },
);

export const updateLastAccessedThunk = createAsyncThunk(
  "pathViewer/updateLastAccessed",
  async ({ itemId }: { itemId: number }, { getState, rejectWithValue }) => {
    try {
      const { enrollment } = requireLoadedEnrollment(
        getState() as ThunkStateSlice,
      );
      return await updateLastAccessed(enrollment.id, itemId);
    } catch (err) {
      return rejectWithValue(
        extractApiError(err, "Failed to update last accessed"),
      );
    }
  },
);

/**
 * Replaces the stored viewer state with a post-completion refetch and queues
 * celebration triggers revealed by the diff (newly completed milestones,
 * pathComplete flip).
 */
const applyCompletionRefetch = (
  state: PathViewerSliceState,
  next: ViewerState,
): void => {
  const { newlyCompletedMilestoneItemIds, pathJustCompleted } =
    diffCelebrations(state.currentPath, next);
  state.currentPath = next;
  state.celebrationMilestoneItemIds = state.celebrationMilestoneItemIds.concat(
    newlyCompletedMilestoneItemIds,
  );
  if (pathJustCompleted) {
    state.pathJustCompleted = true;
  }
};

const pathViewerSlice = createSlice({
  name: "pathViewer",
  initialState,
  reducers: {
    clearCurrentPath: (state) => {
      state.currentPath = null;
      state.error = null;
      state.celebrationMilestoneItemIds = [];
      state.pathJustCompleted = false;
    },
    dismissMilestoneCelebration: (state) => {
      state.celebrationMilestoneItemIds =
        state.celebrationMilestoneItemIds.slice(1);
    },
    dismissPathCompletion: (state) => {
      state.pathJustCompleted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPathViewerState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPathViewerState.fulfilled, (state, action) => {
        state.loading = false;
        // Stored verbatim — server statuses, real timestamps, no celebration
        // diffing on plain loads. Queued celebrations belong to a previously
        // viewed path state: a stale, undismissed queue head whose item is
        // not in the freshly loaded path would never render its modal and
        // would permanently block all later celebrations (M2 review
        // must-fix #4) — plain loads reset the queue.
        state.currentPath = action.payload;
        state.celebrationMilestoneItemIds = [];
        state.pathJustCompleted = false;
      })
      .addCase(loadPathViewerState.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to load learning path";
      })
      .addCase(completePathItemThunk.pending, (state, action) => {
        state.completingItemId = action.meta.arg.itemId;
        state.error = null;
      })
      .addCase(completePathItemThunk.fulfilled, (state, action) => {
        state.completingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completePathItemThunk.rejected, (state, action) => {
        state.completingItemId = null;
        state.error = (action.payload as string) || "Failed to complete item";
      })
      .addCase(completeChecklistItemThunk.pending, (state, action) => {
        state.completingChecklistItemIndex = action.meta.arg.itemIndex;
        state.error = null;
      })
      .addCase(completeChecklistItemThunk.fulfilled, (state, action) => {
        state.completingChecklistItemIndex = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completeChecklistItemThunk.rejected, (state, action) => {
        state.completingChecklistItemIndex = null;
        state.error =
          (action.payload as string) || "Failed to complete checklist item";
      })
      .addCase(completeMeetingTaskThunk.pending, (state, action) => {
        state.completingItemId = action.meta.arg.pathItemId;
        state.error = null;
      })
      .addCase(completeMeetingTaskThunk.fulfilled, (state, action) => {
        state.completingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completeMeetingTaskThunk.rejected, (state, action) => {
        state.completingItemId = null;
        state.error =
          (action.payload as string) || "Failed to complete meeting task";
      })
      .addCase(completeDecisionTaskThunk.pending, (state, action) => {
        state.completingItemId = action.meta.arg.pathItemId;
        state.error = null;
      })
      .addCase(completeDecisionTaskThunk.fulfilled, (state, action) => {
        state.completingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completeDecisionTaskThunk.rejected, (state, action) => {
        state.completingItemId = null;
        state.error =
          (action.payload as string) || "Failed to record your choice";
      })
      .addCase(completeQuizTaskThunk.pending, (state, action) => {
        state.completingItemId = action.meta.arg.pathItemId;
        state.error = null;
      })
      .addCase(completeQuizTaskThunk.fulfilled, (state, action) => {
        state.completingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completeQuizTaskThunk.rejected, (state, action) => {
        state.completingItemId = null;
        state.error =
          (action.payload as string) || "Failed to record your quiz result";
      })
      .addCase(completeProfileTaskThunk.pending, (state, action) => {
        state.completingItemId = action.meta.arg.pathItemId;
        state.error = null;
      })
      .addCase(completeProfileTaskThunk.fulfilled, (state, action) => {
        state.completingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(completeProfileTaskThunk.rejected, (state, action) => {
        state.completingItemId = null;
        state.error =
          (action.payload as string) || "Failed to complete profile task";
      })
      .addCase(uploadPathItemFileThunk.pending, (state, action) => {
        state.uploadingItemId = action.meta.arg.itemId;
        state.error = null;
      })
      .addCase(uploadPathItemFileThunk.fulfilled, (state, action) => {
        state.uploadingItemId = null;
        applyCompletionRefetch(state, action.payload);
      })
      .addCase(uploadPathItemFileThunk.rejected, (state, action) => {
        state.uploadingItemId = null;
        state.error = (action.payload as string) || "Failed to upload file";
      })
      .addCase(
        updateLastAccessedThunk.fulfilled,
        (state, action: PayloadAction<UpdateLastAccessedResponse>) => {
          if (!state.currentPath) return;
          state.currentPath.enrollment.lastAccessedAt =
            action.payload.lastAccessedAt;
          state.currentPath.enrollment.currentItemId =
            action.payload.currentItemId;
          state.currentPath.enrollment.currentStepId =
            action.payload.currentStepId;
        },
      );
    // updateLastAccessedThunk.rejected intentionally not handled: the resume
    // pointer write-back is a background nicety and must never surface an
    // error banner over the viewer.
  },
});

export const {
  clearCurrentPath,
  dismissMilestoneCelebration,
  dismissPathCompletion,
} = pathViewerSlice.actions;
export default pathViewerSlice.reducer;
